import { currenciesSymbol } from '@/helpers/formatCurrency';
import { resolveDefaultBankAccount } from '@/shared/common/utils/bankAccountType.util';
import type { BankAccounts, WalletType } from '@/types/WalletType';
import type { AdditionalAccountWallet } from '../types/additionalAccount.type';
import type {
  CompanyAccountCatalogGroupResponse,
  CompanyAccountCatalogItemResponse,
  CompanyAccountsCatalogResponse,
} from '../types/companyAccountsCatalog.type';
import {
  ACTIVE_ADDITIONAL_ACCOUNT_STATUS,
  UNRESOLVED_ADDITIONAL_ACCOUNT_ROUTE_ID,
} from '../constants/additionalAccount.constant';
import { resolveCurrencyCountryCode, resolveFlagCountryCode } from '../utils/currencyFlag.util';

/**
 * El catálogo solo entrega el número de cuenta; el resto del riel
 * bancario (tipo, routing, banco) es dato a nivel de moneda que se CLONA del
 * bank account default de la wallet principal — de él dependen el "No.
 * cuenta" de WalletItem/WalletDetailHeader (vía `bankAccountInfo`) y el
 * botón "Cargar" del detalle (`isWalletAccount`). Sin principal, sin default
 * en ella o sin `accountNumber`, se degrada a `[]`: sin "No. cuenta" en esas
 * vistas e `isWalletAccount` en false (en MX "Cargar" lo decide igual el flag
 * `enableLocalPaymentNoMx`). La tabla de Gestión no depende del riel: lee
 * `AdditionalAccountWallet.accountNumber`.
 */
function buildSyntheticBankAccounts(
  account: CompanyAccountCatalogItemResponse,
  mainWallet: WalletType | undefined
): BankAccounts[] {
  const mainDefaultBankAccount = resolveDefaultBankAccount(mainWallet?.bankAccounts);
  if (!mainDefaultBankAccount || !account.accountNumber) {
    return [];
  }
  return [{ ...mainDefaultBankAccount, bankAccountNumber: account.accountNumber, defaultAccount: true }];
}

function mapAdditionalAccountItem(
  account: CompanyAccountCatalogItemResponse,
  group: CompanyAccountCatalogGroupResponse,
  mainWallet: WalletType | undefined
): AdditionalAccountWallet {
  const currency = group.currencyCode;
  const countryCode = mainWallet?.countryCode ?? resolveCurrencyCountryCode(currency);
  const currencySymbol =
    currenciesSymbol[currency as keyof typeof currenciesSymbol] ??
    mainWallet?.balance.currencySymbol ??
    group.currencySymbol ??
    '';
  const bankAccounts = buildSyntheticBankAccounts(account, mainWallet);

  return {
    accountName: account.name ?? '',
    createdDate: account.createdDate ?? null,
    accountNumber: account.accountNumber ?? null,
    wallet: {
      walletId: account.id,
      // Fallback para un catálogo sin `createdDate`: el `assignedAt` mock del
      // tab Usuarios solo necesita una fecha ISO parseable, no exacta.
      createdDate: account.createdDate ?? new Date().toISOString(),
      accountBranchName: '',
      currency,
      countryCode,
      flagCountryCode: mainWallet?.flagCountryCode ?? resolveFlagCountryCode(currency),
      isMainAccount: false,
      balance: {
        availableBalance: account.balance ?? 0,
        totalBalance: account.balance ?? 0,
        lockedBalance: 0,
        availableBalanceUsd: account.availableBalanceUsd ?? undefined,
        currency,
        countryCode,
        currencySymbol,
        dateMillis: 0,
      },
      /**
       * Degrada a `''` cuando el catálogo no trae el blob (solo viene en las cuentas asignadas).
       * Sin blob la cuenta no es operable: `filterOperableOriginAccounts` la saca del origen de
       * los cotizadores y de "entre cuentas", que la siguen ofreciendo solo como destino.
       */
      checkDataAccount: account.checkDataAccount ?? '',
      withdrawalAvailable: true,
      accountNumber: 0,
      bankAccountCode: bankAccounts.length > 0 ? (mainWallet?.bankAccountCode ?? null) : null,
      bankAccounts,
      routeId: mainWallet?.routeId ?? UNRESOLVED_ADDITIONAL_ACCOUNT_ROUTE_ID,
      // Obligatorio aunque vacío: `useWalletItem` crashea sin él.
      compensationNumbers: [],
    },
  };
}

/**
 * Filtra y adapta un grupo de moneda. Un usuario puede tener asignadas
 * adicionales de una moneda sin su principal: el grupo NO se descarta, sus
 * cuentas se arman sin riel bancario y con país/bandera derivados de la moneda.
 */
function mapCurrencyGroup(
  group: CompanyAccountCatalogGroupResponse,
  mainWalletsByCurrency: Map<string, WalletType>
): AdditionalAccountWallet[] {
  const mainWallet = mainWalletsByCurrency.get(group.currencyCode);
  return (group.accounts ?? [])
    .filter(
      (account) => account.isAdditional === true && account.accountStatus === ACTIVE_ADDITIONAL_ACCOUNT_STATUS
    )
    .map((account) => mapAdditionalAccountItem(account, group, mainWallet));
}

/**
 * Adapta el catálogo de la empresa (`grouped-by-currency`) al shape que consume
 * toda la UI de cuentas adicionales (`AdditionalAccountWallet` con un
 * `WalletType` completo). Se queda con las adicionales ACTIVE: el backend ya
 * devuelve solo las del usuario, así que no hay otro filtro acá.
 * `mainWalletsByCurrency` aporta, por moneda, los campos que el catálogo no
 * entrega (countryCode/flag, riel bancario, routeId) cuando el usuario tiene la
 * principal; sin ella se derivan de la moneda o se degradan. El cruce va por
 * `Map` para no recorrer la lista de wallets principales por cada grupo.
 */
export function getAdditionalAccountsAdapter(
  response: CompanyAccountsCatalogResponse,
  mainWalletsByCurrency: Map<string, WalletType>
): AdditionalAccountWallet[] {
  // Defensivo ante un 200 fuera de contrato (proxy/gateway): degradar a lista
  // vacía en vez de lanzar — un throw acá rechazaría el fetch del singleton.
  return (response?.currencies ?? []).flatMap((group) => mapCurrencyGroup(group, mainWalletsByCurrency));
}
